import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FeedbackGroupSettings, OnSiteReportSendResult, ReportsSiteSettings } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Alert, Button, Card, CheckboxField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToastOnSuccess } from '../../components/Toast';
import { EmailListInput, TimeInput, isTimeOfDay } from '../../components/phase2';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { useActiveGroups } from './api';
import { useSettingsDraft } from '../../lib/useSettingsDraft';

type SiteSettings = ReturnType<typeof ReportsSiteSettings.parse>;
type FeedbackSettings = ReturnType<typeof FeedbackGroupSettings.parse>;

function SaveButton({ canEdit, changed, valid, pending, onSave }: { canEdit: boolean; changed: boolean; valid: boolean; pending: boolean; onSave: () => void }) {
  if (!canEdit) return null;
  return (
    <Button variant="primary" disabled={!changed || !valid || pending} onClick={onSave}>
      {t('common.save')}
    </Button>
  );
}

/** Site settings card: who is still on site at a set time each day, emailed to a list. "Send now" checks the set-up. */
export function OnSiteReportCard({ scope, scopeId, canEdit }: SettingsCardProps) {
  const siteId = scope === 'sites' ? scopeId : '';
  const s = useSettingsDraft(`/sites/${siteId}/settings`, 'reports', ReportsSiteSettings);
  const groups = useActiveGroups();
  useToastOnSuccess(s.save.isSuccess, t('common.saved'));
  const sendNow = useMutation({ mutationFn: () => api.post(`/reports/on-site/${siteId}/send-now`, { schema: OnSiteReportSendResult }) });
  if (siteId === '') return null;
  if (s.q.isError) return <ErrorPanel error={s.q.error} onRetry={() => void s.q.refetch()} />;
  const d: SiteSettings | null = s.draft;
  if (!d) return <Spinner />;
  const r = d.onSiteReport;
  const set = (patch: Partial<SiteSettings['onSiteReport']>) => s.setDraft({ ...d, onSiteReport: { ...r, ...patch } });
  const timeBad = !isTimeOfDay(r.time);
  const noRecipients = r.enabled && r.recipients.length === 0;
  const toggleGroup = (id: string, on: boolean) => set({ groupIds: on ? [...r.groupIds, id] : r.groupIds.filter((g) => g !== id) });
  const result = sendNow.data;

  return (
    <Card
      title={t('rpt.onsite.title')}
      description={t('rpt.onsite.description')}
      footer={
        <>
          {canEdit ? (
            <Button disabled={s.changed || r.recipients.length === 0 || sendNow.isPending} onClick={() => sendNow.mutate()}>
              {t('rpt.onsite.sendNow')}
            </Button>
          ) : null}
          <SaveButton canEdit={canEdit} changed={s.changed} valid={s.valid && !timeBad && !noRecipients} pending={s.save.isPending} onSave={() => s.save.mutate()} />
        </>
      }
    >
      <div className="space-y-4">
        <ErrorLine error={s.save.error} />
        <CheckboxField label={t('rpt.onsite.enabled')} checked={r.enabled} disabled={!canEdit} onChange={(e) => set({ enabled: e.target.checked })} />
        <TimeInput label={t('rpt.onsite.time')} hint={t('rpt.onsite.timeHint')} value={r.time} disabled={!canEdit} onChange={(time) => set({ time })} />
        <EmailListInput
          label={t('rpt.onsite.recipients')}
          value={r.recipients}
          disabled={!canEdit}
          max={20}
          onChange={(recipients) => set({ recipients })}
          {...(noRecipients ? { hint: t('rpt.onsite.recipientsNeeded') } : {})}
        />
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-800">{t('rpt.onsite.groups')}</legend>
          <p className="text-xs text-slate-600">{t('rpt.onsite.groupsHint')}</p>
          {(groups.data ?? []).map((g) => (
            <CheckboxField key={g.id} label={g.name} checked={r.groupIds.includes(g.id)} disabled={!canEdit} onChange={(e) => toggleGroup(g.id, e.target.checked)} />
          ))}
        </fieldset>
        <CheckboxField label={t('rpt.onsite.weekdaysOnly')} checked={r.weekdaysOnly} disabled={!canEdit} onChange={(e) => set({ weekdaysOnly: e.target.checked })} />
        <CheckboxField label={t('rpt.onsite.sendWhenEmpty')} checked={r.sendWhenEmpty} disabled={!canEdit} onChange={(e) => set({ sendWhenEmpty: e.target.checked })} />
        {s.changed && canEdit ? <p className="text-xs text-slate-600">{t('rpt.onsite.saveFirst')}</p> : null}
        {sendNow.error ? <ErrorLine error={sendNow.error} /> : null}
        {result ? <Alert kind="success">{t('rpt.onsite.sent', { recipients: result.recipients, people: result.people })}</Alert> : null}
      </div>
    </Card>
  );
}

/** Group settings card: the feedback email sent after a visit. The wording fields stay empty to use the standard text. */
export function FeedbackCard({ scope, scopeId, canEdit }: SettingsCardProps) {
  const groupId = scope === 'groups' ? scopeId : '';
  const s = useSettingsDraft(`/groups/${groupId}/settings`, 'feedback', FeedbackGroupSettings);
  const [tried, setTried] = useState(false);
  useToastOnSuccess(s.save.isSuccess, t('common.saved'));
  if (groupId === '') return null;
  if (s.q.isError) return <ErrorPanel error={s.q.error} onRetry={() => void s.q.refetch()} />;
  const d: FeedbackSettings | null = s.draft;
  if (!d) return <Spinner />;
  const set = (patch: Partial<FeedbackSettings>) => s.setDraft({ ...d, ...patch });
  const num = (v: string, min: number, max: number, fallback: number): number => {
    const n = Number(v);
    return Number.isFinite(n) && v !== '' ? Math.min(max, Math.max(min, Math.round(n))) : fallback;
  };
  return (
    <Card
      title={t('rpt.feedback.title')}
      description={t('rpt.feedback.description')}
      footer={
        <SaveButton
          canEdit={canEdit}
          changed={s.changed}
          valid={s.valid}
          pending={s.save.isPending}
          onSave={() => {
            setTried(true);
            s.save.mutate();
          }}
        />
      }
    >
      <div className="space-y-4">
        <ErrorLine error={tried ? s.save.error : null} />
        <CheckboxField label={t('rpt.feedback.enabled')} hint={t('rpt.feedback.enabledHint')} checked={d.enabled} disabled={!canEdit} onChange={(e) => set({ enabled: e.target.checked })} />
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            label={t('rpt.feedback.delay')}
            hint={t('rpt.feedback.delayHint')}
            type="number"
            min={0}
            max={10080}
            disabled={!canEdit}
            value={d.delayMinutes}
            onChange={(e) => set({ delayMinutes: num(e.target.value, 0, 10080, d.delayMinutes) })}
          />
          <TextField
            label={t('rpt.feedback.minDays')}
            hint={t('rpt.feedback.minDaysHint')}
            type="number"
            min={0}
            max={365}
            disabled={!canEdit}
            value={d.minDaysBetween}
            onChange={(e) => set({ minDaysBetween: num(e.target.value, 0, 365, d.minDaysBetween) })}
          />
          <TextField
            label={t('rpt.feedback.lowScore')}
            hint={t('rpt.feedback.lowScoreHint')}
            type="number"
            min={0}
            max={5}
            disabled={!canEdit}
            value={d.lowScoreAtOrBelow}
            onChange={(e) => set({ lowScoreAtOrBelow: num(e.target.value, 0, 5, d.lowScoreAtOrBelow) })}
          />
        </div>
        <TextField label={t('rpt.feedback.question')} hint={t('rpt.feedback.questionHint')} maxLength={200} disabled={!canEdit} value={d.question} onChange={(e) => set({ question: e.target.value })} />
        <CheckboxField label={t('rpt.feedback.allowComment')} checked={d.allowComment} disabled={!canEdit} onChange={(e) => set({ allowComment: e.target.checked })} />
        <TextField label={t('rpt.feedback.subject')} hint={t('rpt.feedback.wordingHint')} maxLength={200} disabled={!canEdit} value={d.subject} onChange={(e) => set({ subject: e.target.value })} />
        <div>
          <label htmlFor="rpt-feedback-body" className="mb-1 block text-sm font-medium text-slate-800">
            {t('rpt.feedback.body')}
          </label>
          <textarea
            id="rpt-feedback-body"
            rows={5}
            maxLength={10000}
            disabled={!canEdit}
            value={d.body}
            onChange={(e) => set({ body: e.target.value })}
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm disabled:bg-slate-100"
          />
          <p className="mt-1 text-xs text-slate-600">{t('rpt.feedback.bodyHint')}</p>
        </div>
      </div>
    </Card>
  );
}
